import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { Organization } from '@/pages/tenancy/types';

/**
 * Can the node THIS session is bound to hold prepaid credit at all?
 *
 * `GET /api/bo/mlm-credit/:id` answers 400 COMM-CREDIT-002 for a structural node
 * (root / Company), which is the right answer — a node with no currency holds no
 * credit book. The callers used to learn it by asking and being refused, so a
 * normal company admin's console opened with a red 400 from a money endpoint on
 * every page load, and one component treated "the request errored" as "the
 * caller is root", which is also true of a network blip.
 *
 * The fact is already on hand: `/api/v1/organizations/me` is fetched for the BO
 * brand, and a structural node is exactly `currency == null` there. Same query
 * key, same staleTime as `useBoBrand`, so this adds no request of its own.
 *
 * `undefined` means "not known yet" — still loading, or the org call failed.
 * Callers must treat that as "do not ask yet" rather than as either answer.
 */
export function useSelfIsStructural(): boolean | undefined {
  const { data, isError } = useQuery({
    queryKey: ['organizations', 'me'],
    queryFn: () => api.get<Organization>('/api/v1/organizations/me'),
    retry: false,
    staleTime: 60_000,
  });
  if (isError) return undefined;
  if (!data) return undefined;
  return data.currency == null;
}
